import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, InfoIcon, Loader2, UserCircle, ClipboardCheck, GraduationCap, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase, serviceSupabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { createHubspotContact } from '@/services/hubspotService';
import { sendWelcomeEmail } from '@/services/emailService';
// Comment out unused imports
// import { isCompanyEmail, isPersonalEmail } from '@/utils/emailValidator';
// import { sendCompanyEmailVerification, sendAdminRecruiterVerificationNotification } from '@/services/emailService';

// Profile Step Component
const ProfileStep: React.FC<{
  profile: any;
  user: any;
  onSubmit: (formData: any) => void;
  isLoading: boolean;
}> = ({ profile, user, onSubmit, isLoading }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: user?.email || '',
    phone: profile?.phone || '',
    role: profile?.role || '',
    organizationName: profile?.organizations?.name || '',
    hasAuthority: false // Add new field for the authority checkbox
  });
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url || null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [authorityError, setAuthorityError] = useState(''); // Add state for checkbox validation error

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [id]: type === 'checkbox' ? checked : value 
    }));
    
    // Clear error message when checkbox is checked
    if (id === 'hasAuthority' && checked) {
      setAuthorityError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate organization name
    if (!formData.organizationName.trim()) {
      toast.error('Organization name is required');
      return;
    }
    
    // Validate authority checkbox
    if (!formData.hasAuthority) {
      setAuthorityError('You must confirm that you have authority to represent this organization');
      return;
    }
    
    onSubmit({ ...formData, avatarFile });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAvatarFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-[#1A1F2C] mb-2">
          Set Up Your Recruiter Profile
        </h1>
        <p className="text-[#1A1F2C]/70 max-w-xl mx-auto">
          Tell us more about yourself to personalize your experience.
        </p>
      </div>
      
      <div>
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center mb-8">
            <div className="relative">
              <Avatar className="h-24 w-24 cursor-pointer border-2 border-[#9b87f5]/20" onClick={() => fileInputRef.current?.click()}>
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} />
                ) : (
                  <AvatarFallback className="bg-[#F8F7FF] text-[#1A1F2C]">
                    <UserCircle className="h-12 w-12" />
                  </AvatarFallback>
                )}
              </Avatar>
              <input 
                type="file" 
                className="hidden" 
                accept="image/*" 
                ref={fileInputRef} 
                onChange={handlePhotoChange}
              />
              <div className="absolute bottom-0 right-0 h-6 w-6 bg-[#9b87f5] rounded-full flex items-center justify-center border-2 border-white" onClick={() => fileInputRef.current?.click()}>
                <span className="text-white text-sm font-bold">+</span>
              </div>
              <div className="mt-2 text-center text-sm text-[#1A1F2C]/70">Profile Photo</div>
            </div>
          </div>

          <Card className="border border-[#9b87f5]/20 bg-[#F8F7FF]">
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input 
                    id="firstName" 
                    placeholder="First Name" 
                    value={formData.firstName} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input 
                    id="lastName" 
                    placeholder="Last Name" 
                    value={formData.lastName} 
                    onChange={handleChange} 
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder={formData.email || "Email"} 
                  value={formData.email} 
                  disabled 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (optional)</Label>
                <Input 
                  id="phone" 
                  placeholder="Phone Number" 
                  value={formData.phone} 
                  onChange={handleChange} 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Job Title (optional)</Label>
                <Input 
                  id="role" 
                  placeholder="Job Title" 
                  value={formData.role} 
                  onChange={handleChange} 
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="organizationName">
                  Organization Name <span className="text-red-500">*</span>
                </Label>
                <Input 
                  id="organizationName" 
                  placeholder="Organization Name" 
                  value={formData.organizationName} 
                  onChange={handleChange} 
                  required
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-start space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="hasAuthority"
                    checked={formData.hasAuthority}
                    onChange={handleChange}
                    className="h-4 w-4 mt-1 rounded border-gray-300 text-[#9b87f5] focus:ring-[#9b87f5]"
                  />
                  <Label 
                    htmlFor="hasAuthority"
                    className="font-normal text-[#1A1F2C] cursor-pointer"
                  >
                    I confirm that I have the authority to represent {formData.organizationName || '[Organization]'} to job candidates
                  </Label>
                </div>
                {authorityError && (
                  <p className="text-sm text-red-500 mt-1">{authorityError}</p>
                )}
              </div>
            </CardContent>
          </Card>
          
          <div className="mt-6 flex justify-end">
            <Button 
              type="submit" 
              className="bg-[#9b87f5] text-white hover:bg-[#8976e9]"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Profile'
              )}
            </Button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, assignBasicPlan, setProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    // Add debug logging
    console.log('🔍 Profile in useEffect:', profile);
    
    // If user already has a complete profile with organization name, redirect to dashboard
    if (profile) {
      const hasOrganizationName = profile?.organizations?.name && profile.organizations.name.trim() !== '';
          
      console.log('🔍 Has organization name:', hasOrganizationName);
      console.log('🔍 Organization name value:', profile?.organizations?.name);
      
      if (hasOrganizationName) {
        console.log('🔄 Redirecting to dashboard due to complete profile');
        navigate('/dashboard');
      }
    }
  }, [profile, navigate]);

  const handleProfileSubmit = async (formData: any) => {
    if (!user) return;
    
    setIsSubmitting(true);
    
    try {
      console.log('🔄 Starting profile submission');
      
      // Organization updates
      try {
        // Get the user's organization ID
        const { data: userData, error: userError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('❌ Error fetching organization details:', userError);
          toast.error('Error fetching organization details');
          return; // Don't throw - continue with profile update
        }

        const organizationId = userData?.organization_id;
        
        // Validate organization
        if (!organizationId) {
          console.error('❌ No organization found for recruiter');
          toast.error('No organization found');
          return; // Don't throw - continue with profile update
        }
        
        console.log('🔄 Updating organization name');
        const { error: orgError } = await supabase
          .from('organizations')
          .update({
            name: formData.organizationName,
            updated_at: new Date().toISOString()
          })
          .eq('id', organizationId);

        if (orgError) {
          console.error('❌ Error updating organization:', orgError);
          toast.error('Error updating organization');
          return; // Don't throw - continue with profile update
        }
        
        // Force refresh the profile to get the updated organization name
        console.log('🔄 Refreshing profile after organization update');
        const { data: refreshedProfile, error: refreshError } = await supabase
          .from('profiles')
          .select('*, organizations(*)')
          .eq('id', user.id)
          .single();
          
        if (!refreshError && refreshedProfile) {
          console.log('✅ Profile refreshed with updated organization:', refreshedProfile.organizations);
          // Update the profile in the context
          setProfile(refreshedProfile);
        }
      } catch (orgError) {
        console.error('❌ Organization update error:', orgError);
        toast.error('Error with organization details');
        // Continue with profile update
      }

      // Update the user profile
      try {
        console.log('🔄 Updating user profile with:', { 
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: formData.role,
          hasAuthority: formData.hasAuthority
        });
        
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: formData.firstName,
            last_name: formData.lastName,
            role: formData.role,
            user_role: 'recruiter', // Set role to recruiter by default
            updated_at: new Date().toISOString(),
            has_organization_authority: formData.hasAuthority
          })
          .eq('id', user.id);

        if (profileError) {
          console.error('❌ Error updating profile:', profileError);
          toast.error('Error updating profile');
          return;
        } else {
          console.log('✅ Profile updated successfully');
          toast.success('Profile updated successfully');

          // Assign basic plan and update HubSpot
          if (user.id) {
            try {
              // Assign the basic plan
              await assignBasicPlan(user.id, user.email || '', {
                first_name: formData.firstName,
                last_name: formData.lastName
              });
              
              // Create HubSpot contact
              try {
                await createHubspotContact(
                  user.email || '',
                  formData.firstName, 
                  formData.lastName,
                  formData.organizationName,
                  user.id,
                  false
                );
              } catch (hubspotError) {
                console.error('❌ Error creating HubSpot contact:', hubspotError);
                // Continue despite errors
              }
              
              // Send welcome email after successful profile setup
              try {
                await sendWelcomeEmail(user.email || '', formData.firstName);
              } catch (emailError) {
                console.error('❌ Failed to send welcome email:', emailError);
                // Continue with flow even if email fails
              }
            } catch (integrationError) {
              console.error('❌ Error with post-profile integrations:', integrationError);
              // Continue even if these steps fail - they're not critical for the user flow
            }
          }
          
          // Force navigation to dashboard immediately after profile update
          console.log('✅ Profile submission complete, forcing redirect to dashboard');
          navigate('/dashboard');
        }
      } catch (profileUpdateError) {
        console.error('❌ Profile update error:', profileUpdateError);
        toast.error('Error updating profile');
      } finally {
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('❌ Failed to save profile:', error);
      toast.error('Failed to save profile');
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FBFAFF] flex flex-col">
      <header className="bg-white border-b border-[#9b87f5]/10 py-4">
        <div className="container mx-auto px-4 flex justify-center">
          <img src="/images/dark-logo.png" alt="RoundOne Logo" className="h-8" />
        </div>
      </header>
      
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="container max-w-4xl mx-auto">
          <ProfileStep 
            profile={profile}
            user={user}
            onSubmit={handleProfileSubmit}
            isLoading={isSubmitting}
          />
        </div>
      </main>
    </div>
  );
};

export default Onboarding; 